/**
 * Recent nav destinations for the ⌘K palette. Stored as a small JSON
 * array of nav paths in localStorage — most recent first, deduped,
 * capped. Storage failures (private mode, quota) are swallowed: recents
 * are a convenience, never a dependency.
 */
const STORAGE_KEY = 'ops.recent-paths';
const MAX_ENTRIES = 8;

/**
 * Read-time migration for renamed nav destinations (#860). Entries were
 * persisted before the customer-manager routes moved to /crm/*; mapping
 * them here keeps an operator's recents alive across the rename without
 * a storage-version bump. Kept alongside the route redirect shims.
 */
const LEGACY_PATH_MAP: Readonly<Record<string, string>> = {
  '/ops/v2/customer-manager': '/crm',
  '/ops/v2/customer-manager/requests': '/crm/requests',
  '/ops/v2/customer-pool': '/crm/customer-pool',
  // The metrics hub absorbed the standalone usage/pnl/sku pages (#1643 S5).
  '/business/revenue': '/business/metrics',
  '/business/supply': '/business/metrics',
  '/customers/usage': '/business/metrics',
};

export function loadRecentPaths(): readonly string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    const mapped = parsed
      .filter((entry): entry is string => typeof entry === 'string')
      .map((entry) => LEGACY_PATH_MAP[entry] ?? entry);
    // Mapping can collide with an already-stored new path; keep first
    // occurrence (most recent) only.
    return [...new Set(mapped)];
  } catch {
    return [];
  }
}

export function pushRecentPath(path: string): void {
  try {
    const next = [path, ...loadRecentPaths().filter((entry) => entry !== path)].slice(
      0,
      MAX_ENTRIES,
    );
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Best-effort only.
  }
}
